import { isEqual } from "lodash";
import { checkDisableMultiple } from "pages/creditBills/utils/actionMaker";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { actions as notificationActions } from "reducers/notification";
import * as config from "util/config";
import * as i18n from "util/i18n";

export const useCheckTable = ({
    list,
    initChecked,
    setChecked,
    key,
    configId,
    i18nId,
    scope,
    validateCheckFn,
    fnArg = [],
}) => {
    const [selectedItems, setSelectedItems] = useState([...initChecked]);
    const dispatch = useDispatch();
    const maxAllowedChecked = config.getInteger(configId, 10);
    const maxAllowedNotification = notificationActions.showNotification(i18n.get(i18nId), "warning", [scope]);
    const commonItems = selectedItems.filter((checkedItem) =>
        list.some((listItem) => isEqual(checkedItem[key], listItem[key])),
    );

    useEffect(() => {
        dispatch(setChecked(commonItems));
    }, [selectedItems]);

    useEffect(() => {
        if (initChecked.length !== commonItems.length) {
            setSelectedItems(commonItems);
        }
    }, [commonItems]);

    const handleCheckAll = useCallback(
        (checked) => {
            let listElements = checked ? selectedItems : [];

            if (checked && selectedItems.length >= maxAllowedChecked) {
                listElements = [];
            } else if (checked) {
                for (let i = 0; i < list.length; i++) {
                    const okToAdd = !validateCheckFn(list[i], ...fnArg);
                    const maxSelectedReached = listElements.length >= maxAllowedChecked;

                    if (
                        okToAdd &&
                        !maxSelectedReached &&
                        !selectedItems.some((item) => isEqual(item[key], list[i][key]))
                    ) {
                        listElements = [...listElements, list[i]];
                    } else if (listElements.length >= maxAllowedChecked) {
                        dispatch(maxAllowedNotification);
                    }
                }
            }

            setSelectedItems(listElements);
        },
        [list, selectedItems, maxAllowedChecked, dispatch, key, ...fnArg],
    );

    const handleSelectRow = useCallback(
        (row) => {
            const okToAdd = !validateCheckFn(row, ...fnArg);
            const isMaxAllowed = selectedItems.length >= maxAllowedChecked;
            const alreadySelected = selectedItems.find((item) => isEqual(item[key], row[key]));
            if (okToAdd) {
                if (!alreadySelected && !isMaxAllowed) {
                    setSelectedItems((prevItems) => [...prevItems, row]);
                } else if (!alreadySelected) {
                    dispatch(maxAllowedNotification);
                }

                if (alreadySelected) {
                    setSelectedItems((prevItems) => prevItems.filter((item) => !isEqual(item[key], row[key])));
                }
            }
        },
        [selectedItems, maxAllowedChecked, dispatch, key, ...fnArg],
    );

    const areAllChecked = useCallback(() => {
        const filterList = list.filter((row) => !checkDisableMultiple(row, ...fnArg));
        if (filterList.length === 0) {
            return () => false;
        }
        return filterList.every((element) => selectedItems.some((item) => isEqual(item[key], element[key])));
    }, [list, selectedItems, key, ...fnArg]);

    return { selectedItems, hookFn: { handleCheckAll, handleSelectRow, areAllChecked } };
};

export default useCheckTable;
